import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@glitz/react";
import { useParams } from "react-router-dom";
import { Product } from "../types";
import { spacing, green, white, grey, lightGrey, fontSize, fontWeight } from "../theme";
import { useViewport } from "../hooks/useViewport";
import ProductCard from "../components/ProductCard";
import Select from "../components/basic/Select";
import Loader from "../components/basic/Loader";
import { BreadCrumbs } from "../components/BreadCrumbs";
import CloseIcon from "../components/CloseIcon";

/** WooCommerce product_brand term-id → visningsnamn (URL kan fortfarande vara /varumarken/635) */
const BRAND_TERM_LABELS: Record<string, string> = {
  "635": "Garmin",
};

function titleFromBrandParam(param: string): string {
  const key = param.trim();
  if (BRAND_TERM_LABELS[key]) return BRAND_TERM_LABELS[key];
  if (/^\d+$/.test(key)) return "Sortiment";
  return key
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const BrandPage: React.FC = () => {
  const { brand } = useParams<{ brand: string }>();
  const { isMobile } = useViewport();
  const [products, setProducts] = useState<Product[]>([]);
  const [sort, setSort] = useState("name");
  const [loading, setLoading] = useState(true);
  const [isSortOpen, setSortOpen] = useState(false);

  useEffect(() => {
    if (!brand) return;
    setProducts([]);
    setLoading(true);

    const fetchProducts = async () => {
      try {
        const res = await fetch(
          `/api/products/by-brand/${encodeURIComponent(brand)}`
        );
        const data = await res.json();
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch products by brand:", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [brand]);

  const brandTitle = useMemo(
    () => (brand ? titleFromBrandParam(brand) : ""),
    [brand]
  );

  const sortOptions = [
    { value: "name", label: "Namn (A–Ö)" },
    { value: "priceLow", label: "Lägsta pris" },
    { value: "priceHigh", label: "Högsta pris" },
  ];

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sort) {
      case "priceLow":
        return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case "priceHigh":
        return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      case "name":
      default:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [sort, products]);

  if (!brand) {
    return null;
  }

  return (
    <PageWrapper className="brand-page">
      <BreadCrumbs variant="brand" title={brandTitle} />
      <TopWrapper className="brand-top" css={isMobile ? topWrapperMobile : undefined}>
        <BrandHeader>{brandTitle}</BrandHeader>
        {isMobile ? (
          <MobileTopRight>
            <SortButton
              type="button"
              className="brand-sort-btn"
              onClick={() => setSortOpen(true)}
            >
              {sortOptions.find((o) => o.value === sort)?.label ?? "Sortera"}
            </SortButton>
          </MobileTopRight>
        ) : (
          <div className="brand-sort">
            <SortSelect
              onChange={(val) => setSort(val)}
              options={sortOptions}
              label="Sortera"
              value={sort}
            />
          </div>
        )}
      </TopWrapper>
      <ProductWrapper className="brand-product-wrapper">
        {loading && <Loader ariaLabel="Laddar produkter" />}
        {!loading &&
          sortedProducts?.length > 0 &&
          sortedProducts?.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        {!loading && sortedProducts?.length === 0 && (
          <div>Inga produkter hittades</div>
        )}
      </ProductWrapper>

      {isMobile && (
        <>
          {isSortOpen && <FilterOverlay onClick={() => setSortOpen(false)} />}
          <SortFlyout
            className={isSortOpen ? "category-filter-open" : "category-filter-closed"}
          >
            <FilterFlyoutTop>
              <FilterFlyoutTitle>Sortera</FilterFlyoutTitle>
              <CloseIcon onClose={() => setSortOpen(false)} />
            </FilterFlyoutTop>
            <FilterFlyoutList>
              {sortOptions.map((opt) => (
                <FilterFlyoutItem
                  key={opt.value}
                  onClick={() => {
                    setSort(opt.value);
                    setSortOpen(false);
                  }}
                >
                  {opt.label}
                </FilterFlyoutItem>
              ))}
            </FilterFlyoutList>
          </SortFlyout>
        </>
      )}
    </PageWrapper>
  );
};

const PageWrapper = styled.div({
  paddingLeft: spacing.m,
  minHeight: "60vh",
});

const TopWrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: spacing.xl,
});

const topWrapperMobile = {
  flexDirection: "column" as const,
  alignItems: "stretch" as const,
  gap: spacing.m,
};

const BrandHeader = styled.h1({});

const SortSelect = styled(Select, {
  width: "160px",
  minWidth: "140px",
  flexShrink: 0,
  marginRight: spacing.m,
});

const MobileTopRight = styled.div({
  display: "flex",
  alignItems: "stretch",
  gap: spacing.s,
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
});

const SortButton = styled.button({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: { x: spacing.m, y: spacing.s },
  border: { xy: { style: "solid", color: green, width: "2px" } },
  borderRadius: "6px",
  backgroundColor: white,
  color: green,
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
  ":hover": {
    backgroundColor: lightGrey,
  },
});

const FilterOverlay = styled.div({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.4)",
  zIndex: 1199,
});

const SortFlyout = styled.div({
  position: "fixed",
  top: 0,
  right: 0,
  width: "300px",
  maxWidth: "85vw",
  height: "100vh",
  backgroundColor: white,
  zIndex: 1200,
  display: "flex",
  flexDirection: "column",
  boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
  transitionProperty: "transform",
  transitionDuration: "0.3s",
  transitionTimingFunction: "ease-in-out",
});

const FilterFlyoutTop = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: { x: spacing.m, y: spacing.s },
  borderBottom: { style: "solid", color: grey, width: "1px" },
});

const FilterFlyoutTitle = styled.h2({
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
});

const FilterFlyoutList = styled.nav({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  flex: { grow: 1, shrink: 1, basis: 0 },
});

const FilterFlyoutItem = styled.div({
  padding: { x: spacing.l, y: spacing.m },
  cursor: "pointer",
  fontSize: fontSize.m,
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
  ":hover": {
    backgroundColor: lightGrey,
  },
});

const ProductWrapper = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: spacing.m,
  marginBottom: spacing.m,
});

export default BrandPage;
